/**
 * Everything come by, page by page.
 *
 * The pages are the world's own: a page for each creature with something to
 * drop and one for each skill's catches and makings, so a drop table changed
 * in the tools is a page changed here. What is filled in is what this
 * character has had, and how many.
 */

import { useMemo, useState } from 'react';
import type { CollectionWire, ItemDefinitionWire } from '@inverse/protocol';
import { CloseButton } from './CloseButton';
import { useDraggableWindow } from './draggable';

const GROUPS = ['Bosses', 'Creatures', 'Gathering', 'Making'] as const;

function initials(name: string): string {
  const words = name.split(' ').filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) return (words[0] ?? '').slice(0, 2).toUpperCase();
  return `${(words[0] ?? '').slice(0, 1)}${(words[1] ?? '').slice(0, 1)}`.toUpperCase();
}

function tint(itemId: number): string {
  return `hsl(${(itemId * 47) % 360} 38% 38%)`;
}

export function CollectionLog({
  log,
  describe,
  onClose,
}: {
  log: CollectionWire;
  describe: (itemId: number) => ItemDefinitionWire | undefined;
  onClose: () => void;
}): JSX.Element {
  const { attach, style, onPointerDown } = useDraggableWindow('collection');
  const groups = GROUPS.filter((group) => log.pages.some((page) => page.group === group));
  const [group, setGroup] = useState<(typeof GROUPS)[number]>(groups[0] ?? 'Creatures');
  const pages = log.pages.filter((page) => page.group === group);
  const [chosen, setChosen] = useState(0);
  const page = pages[Math.min(chosen, pages.length - 1)];

  const had = (id: number): number => log.have[String(id)] ?? 0;
  const totals = useMemo(() => {
    const all = new Set(log.pages.flatMap((one) => one.items));
    let got = 0;
    for (const id of all) if ((log.have[String(id)] ?? 0) > 0) got++;
    return { got, of: all.size };
  }, [log]);

  return (
    <div className="trade collection-log" ref={attach} style={style}>
      <div className="panel-head drag-handle" onPointerDown={onPointerDown}>
        <span>Collection log</span>
        <span className="panel-count">
          {totals.got}/{totals.of}
        </span>
        <CloseButton onClose={onClose} />
      </div>
      <div className="bank-tabs">
        {groups.map((one) => (
          <button
            key={one}
            type="button"
            className={group === one ? 'bank-tab on' : 'bank-tab'}
            onClick={() => {
              setGroup(one);
              setChosen(0);
            }}
          >
            {one}
          </button>
        ))}
      </div>
      <div className="collection-body">
        <div className="collection-pages">
          {pages.map((one, at) => {
            const got = one.items.filter((id) => had(id) > 0).length;
            return (
              <button
                key={`${one.group}:${one.title}`}
                type="button"
                className={page === one ? 'collection-page on' : 'collection-page'}
                onClick={() => setChosen(at)}
              >
                <span>{one.title}</span>
                <small className={got === one.items.length ? 'done' : undefined}>
                  {got}/{one.items.length}
                </small>
              </button>
            );
          })}
          {pages.length === 0 ? <p className="hint">Nothing here yet.</p> : null}
        </div>
        {page ? (
          <div className="collection-items">
            <h4>{page.title}</h4>
            <div className="trade-grid">
              {page.items.map((id) => {
                const name = describe(id)?.name ?? `Item ${id}`;
                const count = had(id);
                return (
                  <div
                    key={id}
                    className={count > 0 ? 'slot filled' : 'slot filled collection-missing'}
                    title={count > 0 ? `${name}: ${count.toLocaleString()}` : `${name}: not yet`}
                  >
                    <span className="slot-icon" style={{ background: tint(id) }}>
                      {initials(name)}
                    </span>
                    {count > 0 ? (
                      <span className="slot-quantity">{count.toLocaleString()}</span>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
